import {
    getSocialPosts,
    getSocialReviewerQueue,
    getSocialPost,
    requestSocialPublish,
    rerenderSocialChannel,
    resolveSocialPostizOutcome,
    regenerateSocialPost,
    cancelSocialPost,
    deleteSocialPost,
    getSocialSettings,
    updateSocialSettings,
    getSocialHealth,
    getSocialStorritoAccounts,
    getSocialExtensionKey,
    rotateSocialExtensionKey,
    getSocialTriggers,
    getSocialCatalog,
    getSocialWorkflowBySlug,
    getSocialWorkflows,
    createSocialWorkflow,
    saveSocialWorkflowDraft,
    publishSocialWorkflow,
    discardSocialWorkflowDraft,
    duplicateSocialWorkflow,
    setSocialWorkflowEnabled,
    renameSocialWorkflow,
    deleteSocialWorkflow,
    getSocialTriggerLog,
    getSocialTriggerEntities,
    simulateSocialWorkflow,
    getSocialSimulation,
    runManualSocialWorkflow,
} from '../../util/api';
import { rerenderWithPhoto } from '../AdminSocialReviewerPage/reviewPhotoApi';
import { parse } from '../../util/urlHelpers';
import { isBulkPublishableChannel } from './postizOutcome';
import { isReviewerQueuePost } from './reviewerQueuePost';
// The one definition of "Postiz took it, the platform has not confirmed yet".
// It lives next to the copy that explains that state to the operator, so the
// predicate the poll stops on and the sentence the reviewer shows can never
// describe two different things.
import { postAwaitsPostizConfirmation } from '../AdminSocialReviewerPage/postizPending';
// Shared pure modules (also loaded server-side through CommonJS).
const { buildDefaultPreset } = require('./DesignEditor/shared/defaultTemplates');
const { validateDesignDoc } = require('./DesignEditor/shared/designDoc');
const { normalizePresets, findPreset, makePreset } = require('./DesignEditor/shared/presets');
// Aspect-aware canvas rescale (full-bleed layers stay full-bleed, everything else
// scales uniformly and re-anchors) — a pure module so the format-switch math is testable.
const { rescaleDocToCanvas } = require('./DesignEditor/canvasRescale');

/**
 * AdminSocialPostsPage duck — the single source of truth for the social
 * automation dashboard. Data flows through the store (modelled on
 * ManageListingsPage.duck), and approve/regenerate/cancel apply optimistically
 * so the reviewer sees the change land immediately and we reconcile from the
 * server's answer ("design for instant").
 *
 * Note: the social API is admin-cookie authenticated, so loadData is a no-op on
 * the server (no browser session) and the real fetch runs on the client — the
 * dashboard dispatches it on mount.
 */

// ================ Action types ================ //

export const QUERY_POSTS_REQUEST = 'app/AdminSocialPostsPage/QUERY_POSTS_REQUEST';
export const QUERY_POSTS_SUCCESS = 'app/AdminSocialPostsPage/QUERY_POSTS_SUCCESS';
export const QUERY_POSTS_ERROR = 'app/AdminSocialPostsPage/QUERY_POSTS_ERROR';

export const PENDING_COUNT_SUCCESS = 'app/AdminSocialPostsPage/PENDING_COUNT_SUCCESS';

export const FETCH_POST_REQUEST = 'app/AdminSocialPostsPage/FETCH_POST_REQUEST';
export const FETCH_POST_SUCCESS = 'app/AdminSocialPostsPage/FETCH_POST_SUCCESS';
export const FETCH_POST_ERROR = 'app/AdminSocialPostsPage/FETCH_POST_ERROR';
export const CLEAR_SELECTED_POST = 'app/AdminSocialPostsPage/CLEAR_SELECTED_POST';

// The post-poll lane (publish / regenerate / Postiz-confirmation follow). See
// the "Cancellable, backing-off polls" section for why run identity lives in
// the store rather than in a module variable.
export const POST_POLL_START = 'app/AdminSocialPostsPage/POST_POLL_START';
export const POST_POLL_END = 'app/AdminSocialPostsPage/POST_POLL_END';
export const CANCEL_POST_POLL = 'app/AdminSocialPostsPage/CANCEL_POST_POLL';

export const POST_ACTION_REQUEST = 'app/AdminSocialPostsPage/POST_ACTION_REQUEST';
export const POST_ACTION_SUCCESS = 'app/AdminSocialPostsPage/POST_ACTION_SUCCESS';
export const POST_ACTION_ERROR = 'app/AdminSocialPostsPage/POST_ACTION_ERROR';
export const CLEAR_ACTION_ERROR = 'app/AdminSocialPostsPage/CLEAR_ACTION_ERROR';

export const DELETE_POST_REQUEST = 'app/AdminSocialPostsPage/DELETE_POST_REQUEST';
export const DELETE_POST_SUCCESS = 'app/AdminSocialPostsPage/DELETE_POST_SUCCESS';
export const DELETE_POST_ERROR = 'app/AdminSocialPostsPage/DELETE_POST_ERROR';

export const FETCH_SETTINGS_REQUEST = 'app/AdminSocialPostsPage/FETCH_SETTINGS_REQUEST';
export const FETCH_SETTINGS_SUCCESS = 'app/AdminSocialPostsPage/FETCH_SETTINGS_SUCCESS';
export const FETCH_SETTINGS_ERROR = 'app/AdminSocialPostsPage/FETCH_SETTINGS_ERROR';

export const SAVE_SETTINGS_REQUEST = 'app/AdminSocialPostsPage/SAVE_SETTINGS_REQUEST';
export const SAVE_SETTINGS_SUCCESS = 'app/AdminSocialPostsPage/SAVE_SETTINGS_SUCCESS';
export const SAVE_SETTINGS_ERROR = 'app/AdminSocialPostsPage/SAVE_SETTINGS_ERROR';

export const FETCH_HEALTH_SUCCESS = 'app/AdminSocialPostsPage/FETCH_HEALTH_SUCCESS';
export const FETCH_HEALTH_ERROR = 'app/AdminSocialPostsPage/FETCH_HEALTH_ERROR';

export const FETCH_TRIGGERS_SUCCESS = 'app/AdminSocialPostsPage/FETCH_TRIGGERS_SUCCESS';
export const FETCH_CATALOG_SUCCESS = 'app/AdminSocialPostsPage/FETCH_CATALOG_SUCCESS';
export const FETCH_CATALOG_ERROR = 'app/AdminSocialPostsPage/FETCH_CATALOG_ERROR';

export const FETCH_WORKFLOW_REQUEST = 'app/AdminSocialPostsPage/FETCH_WORKFLOW_REQUEST';
export const FETCH_WORKFLOW_SUCCESS = 'app/AdminSocialPostsPage/FETCH_WORKFLOW_SUCCESS';
export const FETCH_WORKFLOW_ERROR = 'app/AdminSocialPostsPage/FETCH_WORKFLOW_ERROR';

// A graph edit pushes the prior graph onto the undo stack (past/present/future).
export const WORKFLOW_EDIT = 'app/AdminSocialPostsPage/WORKFLOW_EDIT';
export const WORKFLOW_LAYOUT_SYNC = 'app/AdminSocialPostsPage/WORKFLOW_LAYOUT_SYNC';
export const WORKFLOW_UNDO = 'app/AdminSocialPostsPage/WORKFLOW_UNDO';
export const WORKFLOW_REDO = 'app/AdminSocialPostsPage/WORKFLOW_REDO';

// Autosave the studio's working graph as this workflow's DRAFT (debounced in the
// studio component; the thunk is one PUT). Shape-checked only server-side.
export const SAVE_DRAFT_REQUEST = 'app/AdminSocialPostsPage/SAVE_DRAFT_REQUEST';
export const SAVE_DRAFT_SUCCESS = 'app/AdminSocialPostsPage/SAVE_DRAFT_SUCCESS';
export const SAVE_DRAFT_ERROR = 'app/AdminSocialPostsPage/SAVE_DRAFT_ERROR';

// Publish the draft: THE strict validate gate (400 → German message inline). On
// success the workflow row is replaced (draft cleared, graph = the new published).
export const PUBLISH_WORKFLOW_REQUEST = 'app/AdminSocialPostsPage/PUBLISH_WORKFLOW_REQUEST';
export const PUBLISH_WORKFLOW_SUCCESS = 'app/AdminSocialPostsPage/PUBLISH_WORKFLOW_SUCCESS';
export const PUBLISH_WORKFLOW_ERROR = 'app/AdminSocialPostsPage/PUBLISH_WORKFLOW_ERROR';

// "Zurücksetzen": discard the draft — revert the canvas to the published graph.
export const DISCARD_DRAFT_REQUEST = 'app/AdminSocialPostsPage/DISCARD_DRAFT_REQUEST';
export const DISCARD_DRAFT_SUCCESS = 'app/AdminSocialPostsPage/DISCARD_DRAFT_SUCCESS';
export const DISCARD_DRAFT_ERROR = 'app/AdminSocialPostsPage/DISCARD_DRAFT_ERROR';

// Workflow LIST (the fullscreen Workflow tab): every workflow (slug-keyed rows),
// with an optimistic enable/disable Switch per row.
export const FETCH_WORKFLOWS_REQUEST = 'app/AdminSocialPostsPage/FETCH_WORKFLOWS_REQUEST';
export const FETCH_WORKFLOWS_SUCCESS = 'app/AdminSocialPostsPage/FETCH_WORKFLOWS_SUCCESS';
export const FETCH_WORKFLOWS_ERROR = 'app/AdminSocialPostsPage/FETCH_WORKFLOWS_ERROR';

// Optimistic enable toggle: flip the row immediately, reconcile on success, roll
// back to the prior value on error (design-for-instant).
export const SET_WORKFLOW_ENABLED_OPTIMISTIC =
    'app/AdminSocialPostsPage/SET_WORKFLOW_ENABLED_OPTIMISTIC';
export const SET_WORKFLOW_ENABLED_SUCCESS = 'app/AdminSocialPostsPage/SET_WORKFLOW_ENABLED_SUCCESS';
export const SET_WORKFLOW_ENABLED_ERROR = 'app/AdminSocialPostsPage/SET_WORKFLOW_ENABLED_ERROR';

export const RENAME_WORKFLOW_REQUEST = 'app/AdminSocialPostsPage/RENAME_WORKFLOW_REQUEST';
export const RENAME_WORKFLOW_SUCCESS = 'app/AdminSocialPostsPage/RENAME_WORKFLOW_SUCCESS';
export const RENAME_WORKFLOW_ERROR = 'app/AdminSocialPostsPage/RENAME_WORKFLOW_ERROR';

// Create a blank, paused workflow ("+ Neuer Workflow"). The thunk resolves the
// new row so the caller navigates straight into its studio.
export const CREATE_WORKFLOW_REQUEST = 'app/AdminSocialPostsPage/CREATE_WORKFLOW_REQUEST';
export const CREATE_WORKFLOW_SUCCESS = 'app/AdminSocialPostsPage/CREATE_WORKFLOW_SUCCESS';
export const CREATE_WORKFLOW_ERROR = 'app/AdminSocialPostsPage/CREATE_WORKFLOW_ERROR';

// Duplicate a workflow's published graph into a new, paused workflow. Resolves
// the new row so the caller navigates into it.
export const DUPLICATE_WORKFLOW_REQUEST = 'app/AdminSocialPostsPage/DUPLICATE_WORKFLOW_REQUEST';
export const DUPLICATE_WORKFLOW_SUCCESS = 'app/AdminSocialPostsPage/DUPLICATE_WORKFLOW_SUCCESS';
export const DUPLICATE_WORKFLOW_ERROR = 'app/AdminSocialPostsPage/DUPLICATE_WORKFLOW_ERROR';

// Hard-delete a workflow (409 while posts it minted are still in flight).
export const DELETE_WORKFLOW_REQUEST = 'app/AdminSocialPostsPage/DELETE_WORKFLOW_REQUEST';
export const DELETE_WORKFLOW_SUCCESS = 'app/AdminSocialPostsPage/DELETE_WORKFLOW_SUCCESS';
export const DELETE_WORKFLOW_ERROR = 'app/AdminSocialPostsPage/DELETE_WORKFLOW_ERROR';

// Trigger DECISION log (the per-workflow "Aktivität" panel): recent fired/skipped
// decisions, keyed by trigger, fetched on panel expand.
export const FETCH_TRIGGER_LOG_REQUEST = 'app/AdminSocialPostsPage/FETCH_TRIGGER_LOG_REQUEST';
export const FETCH_TRIGGER_LOG_SUCCESS = 'app/AdminSocialPostsPage/FETCH_TRIGGER_LOG_SUCCESS';
export const FETCH_TRIGGER_LOG_ERROR = 'app/AdminSocialPostsPage/FETCH_TRIGGER_LOG_ERROR';

// Workflow Studio active run. After a manual production run starts, the studio
// follows that exact SocialPost row by id until it reaches the review gate or a
// terminal state. No trigger-wide "latest run" guessing.
export const FETCH_ACTIVE_RUN_REQUEST = 'app/AdminSocialPostsPage/FETCH_ACTIVE_RUN_REQUEST';
export const FETCH_ACTIVE_RUN_SUCCESS = 'app/AdminSocialPostsPage/FETCH_ACTIVE_RUN_SUCCESS';
export const FETCH_ACTIVE_RUN_ERROR = 'app/AdminSocialPostsPage/FETCH_ACTIVE_RUN_ERROR';

// Test Mode ("Testmodus"). A sandbox run of the live (even unsaved) graph against
// a chosen trigger entity — produces an in-canvas `simRun` (post-shaped) with
// ZERO side effects (no review post, no email, no publish). Mutually exclusive
// with the active manual run so the run-state viz has one honest source at a time.
export const SET_TEST_MODE = 'app/AdminSocialPostsPage/SET_TEST_MODE';
export const SET_TRIGGER_ENTITY = 'app/AdminSocialPostsPage/SET_TRIGGER_ENTITY';
export const FETCH_TRIGGER_ENTITIES_REQUEST =
    'app/AdminSocialPostsPage/FETCH_TRIGGER_ENTITIES_REQUEST';
export const FETCH_TRIGGER_ENTITIES_SUCCESS =
    'app/AdminSocialPostsPage/FETCH_TRIGGER_ENTITIES_SUCCESS';
export const FETCH_TRIGGER_ENTITIES_ERROR = 'app/AdminSocialPostsPage/FETCH_TRIGGER_ENTITIES_ERROR';
export const SIMULATE_REQUEST = 'app/AdminSocialPostsPage/SIMULATE_REQUEST';
export const SIMULATE_SUCCESS = 'app/AdminSocialPostsPage/SIMULATE_SUCCESS';
export const SIMULATE_ERROR = 'app/AdminSocialPostsPage/SIMULATE_ERROR';
// "Testlauf abbrechen" — and the same action every implicit exit fires, so a run
// can never outlive the editor that started it.
export const SIMULATE_CANCEL = 'app/AdminSocialPostsPage/SIMULATE_CANCEL';
export const MANUAL_RUN_REQUEST = 'app/AdminSocialPostsPage/MANUAL_RUN_REQUEST';
export const MANUAL_RUN_SUCCESS = 'app/AdminSocialPostsPage/MANUAL_RUN_SUCCESS';
export const MANUAL_RUN_ERROR = 'app/AdminSocialPostsPage/MANUAL_RUN_ERROR';

// Shared design edit state. Review mode edits a per-post doc here; workflow
// design nodes use the preset library stored in settings.designPresets.
export const PRESETS_LOADED = 'app/AdminSocialPostsPage/PRESETS_LOADED';
export const DESIGN_LOAD = 'app/AdminSocialPostsPage/DESIGN_LOAD';
export const DESIGN_EDIT = 'app/AdminSocialPostsPage/DESIGN_EDIT';
export const DESIGN_UNDO = 'app/AdminSocialPostsPage/DESIGN_UNDO';
export const DESIGN_REDO = 'app/AdminSocialPostsPage/DESIGN_REDO';

export const SAVE_DESIGN_REQUEST = 'app/AdminSocialPostsPage/SAVE_DESIGN_REQUEST';
export const SAVE_DESIGN_SUCCESS = 'app/AdminSocialPostsPage/SAVE_DESIGN_SUCCESS';
export const SAVE_DESIGN_ERROR = 'app/AdminSocialPostsPage/SAVE_DESIGN_ERROR';

export const DEFAULT_SOCIAL_POSTS_TAB = 'pending';
export const SOCIAL_POST_TAB_STATUSES = {
    // 'queued'/'generating' are in-flight: showing them here means a freshly-fired
    // trigger is VISIBLE (as "Wird erstellt…") instead of looking like it never
    // fired. 'awaiting_manual' stays visible on the broad dashboard because it may
    // be Community extension work. The separate reviewer API admits only held
    // Stories, so these dashboard statuses must not be reused for reviewer nav.
    pending: ['queued', 'generating', 'pending_approval', 'awaiting_manual'],
    published: ['published'],
    failed: ['failed', 'partially_failed'],
    all: [],
};
export const SOCIAL_POST_UTILITY_TABS = ['workflow', 'settings'];

export const isValidSocialPostsRouteTab = tab =>
    !tab ||
    Object.prototype.hasOwnProperty.call(SOCIAL_POST_TAB_STATUSES, tab) ||
    SOCIAL_POST_UTILITY_TABS.includes(tab);

export const socialPostTabStatuses = tab => {
    if (Object.prototype.hasOwnProperty.call(SOCIAL_POST_TAB_STATUSES, tab)) {
        return SOCIAL_POST_TAB_STATUSES[tab];
    }
    if (!tab) return SOCIAL_POST_TAB_STATUSES[DEFAULT_SOCIAL_POSTS_TAB];
    return null;
};

// The reviewer-queue contract lives in its own zero-import module (pure view
// helpers read it without pulling this duck's API layer in), and is re-exported
// here because the thunks below and every existing importer reach for it from the
// duck. ONE definition, two convenient doors.
export { isReviewerQueuePost };

// ================ Reducer ================ //

const initialState = {
    posts: [],
    total: 0,
    queryInProgress: false,
    queryError: null,
    queryKey: null,

    pendingCount: 0,

    selectedPost: null,
    fetchPostInProgress: false,
    fetchPostError: null,
    fetchPostErrorStatus: null,
    // Identity of the detail route currently allowed to own selectedPost. Publish
    // polling continues in the background for up to two minutes; without this
    // fence a late response for the post we just left can replace the newly-opened
    // post (or clear its loading state).
    fetchPostTargetId: null,
    fetchPostRequestKey: null,
    // Identity of the post poll (publish / regenerate / Postiz-confirmation
    // follow) that currently owns the post-poll lane. null = nothing is
    // following a post, so every loop still running must stop at its next
    // checkpoint.
    postPollRunId: null,

    actionInProgress: null, // id of the post currently being approved/regenerated/cancelled
    actionError: null,
    deletingPostIds: [],

    settings: null,
    fetchSettingsInProgress: false,
    fetchSettingsError: null,
    saveSettingsInProgress: false,
    saveSettingsError: null,
    settingsSaved: false,

    health: null,
    runtimeMode: null,
    healthError: null,

    // Workflow editor (Phase 2). `workflow` is the loaded row metadata
    // (id/name/trigger/version/active); `graph` is the live present graph the
    // editor renders. Undo/redo is a past/present/future snapshot stack of the
    // graph JSON (React Flow ships no history). `workflowDirty` flips on the
    // first edit and clears on a confirmed save.
    triggers: [],
    catalogDefaults: {},
    catalogNodes: null,

    // Workflow LIST (fullscreen Workflow tab) — every workflow (slug-keyed rows),
    // with in-flight sets so a row's Switch/rename/delete shows pending without
    // blocking.
    workflows: [],
    fetchWorkflowsInProgress: false,
    fetchWorkflowsError: null,
    workflowsTogglingSlugs: [],
    workflowsRenamingSlugs: [],
    workflowsError: null,
    renameWorkflowError: null,
    createWorkflowInProgress: false,
    createWorkflowError: null,
    duplicateWorkflowInProgress: false,
    duplicateWorkflowError: null,
    deletingWorkflowSlugs: [],
    deleteWorkflowError: null,

    // Trigger decision log, keyed by workflow SLUG (the per-row "Aktivität" panel).
    triggerLogBySlug: {},
    triggerLogLoadingSlugs: [],
    triggerLogError: null,

    // The studio's currently-loaded workflow slug (its permanent identity).
    workflowSlug: null,
    workflow: null,
    graph: null,
    graphPast: [],
    graphFuture: [],
    workflowDirty: false,
    fetchWorkflowInProgress: false,
    fetchWorkflowError: null,

    // Draft autosave lifecycle (the studio's status chip: Speichert… / Gespeichert
    // ✓ / error). draftSavedAt is the last successful autosave timestamp.
    saveDraftInProgress: false,
    saveDraftError: null,
    draftSavedAt: null,

    // Publish (the strict validate gate) + discard-draft ("Zurücksetzen").
    publishInProgress: false,
    publishError: null,
    discardDraftInProgress: false,
    discardDraftError: null,

    // Workflow Studio active run: the exact REAL post started from this editor,
    // or null when no manual run is selected yet.
    activeRun: null,
    fetchActiveRunInProgress: false,

    // Test Mode ("Testmodus"): a sandbox loop with no side effects. The chosen
    // trigger entity is shared with normal mode; mode only changes whether the
    // graph is simulated or a durable production post is queued.
    testMode: false,
    selectedTriggerEntity: null,
    triggerEntities: [], // finder candidates for the active trigger
    triggerEntitiesSupported: true,
    triggerEntitiesPageInfo: { page: 1, perPage: 24, hasMore: false },
    triggerEntitiesDegraded: false,
    triggerEntitiesDegradedMessage: null,
    triggerEntitiesLookup: null, // exact-ID lookup envelope { id, matched, reason, message }
    triggerEntitiesRequestKey: null,
    triggerEntityLabels: {
        selectorLabel: 'Eintrag',
        selectorPlaceholder: 'Eintrag suchen...',
        selectorScope: '',
        unsupportedMessage: 'Für diesen Auslöser gibt es noch keine Daten-Quelle.',
        emptyMessage: 'Keine passenden Einträge gefunden.',
    },
    fetchTriggerEntitiesInProgress: false,
    fetchTriggerEntitiesError: null,
    simRun: null, // post-shaped { status, channels } from a Testlauf (or { status:'generating' })
    simulateInProgress: false,
    simulateError: null,
    // Identity of the Testlauf that owns the editor. Every terminal simulation
    // action carries the run id it belongs to and is ignored unless it still
    // matches, so a run that was cancelled — or superseded by leaving Testmodus,
    // switching workflow or picking another fixture — can never paint its result
    // into whatever the operator is looking at now. null = no run is active.
    simulateRunId: null,
    // When the active Testlauf started, so the UI can show elapsed time next to
    // a cancel action instead of an inert "Simuliere…".
    simulateStartedAt: null,
    manualRunInProgress: false,
    manualRunError: null,
    // Why the server did not start the last Live-Lauf right away ('busy' |
    // 'resource'); the post stays queued and the background job starts it later.
    manualRunRefusal: null,

    // Shared design state for workflow preset library reads and reviewer edits.
    designPresets: [],
    presetsLoaded: false,
    designDoc: null,
    designPast: [],
    designFuture: [],
    designDirty: false,
    designSaved: false,
    saveDesignInProgress: false,
    saveDesignError: null,
};

/**
 * "No Testlauf owns the editor any more" — one fact, written in ONE place and
 * spread by every path that ends a run (finished, failed, cancelled, Testmodus
 * off, fixture changed, another workflow loaded). Keeping the four fields in
 * step by hand is exactly how `simulateInProgress` came to survive
 * `SET_TEST_MODE` and strand the button behind a 30-minute poll.
 */
const NO_ACTIVE_SIMULATION = {
    simulateRunId: null,
    simulateStartedAt: null,
    simulateInProgress: false,
    simRun: null,
};

// Cap the history depth so a long editing session never grows unbounded.
const MAX_HISTORY = 50;
const pushPast = (past, present) => [...past, present].slice(-MAX_HISTORY);

// Replace a post inside the list (if present) so the list stays consistent
// with an optimistic / server-confirmed detail update.
const replaceInList = (posts, updated) =>
    posts.map(p => (String(p.id) === String(updated.id) ? updated : p));

const removeFromList = (posts, id) => posts.filter(p => String(p.id) !== String(id));

const restoreToList = (posts, post) => {
    if (!post || posts.some(p => String(p.id) === String(post.id))) return posts;
    return [...posts, post].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

// ---- Workflow-list helpers (keyed by slug: the workflow's permanent identity) ----
const setWorkflowEnabledInList = (workflows, slug, enabled) =>
    workflows.map(w => (w.slug === slug ? { ...w, enabled } : w));

const replaceWorkflowInList = (workflows, updated) =>
    workflows.map(w => (w.slug === updated.slug ? { ...w, ...updated } : w));

const upsertWorkflowInList = (workflows, updated) =>
    workflows.some(w => w.slug === updated.slug)
        ? replaceWorkflowInList(workflows, updated)
        : [...workflows, updated].sort((a, b) => ((a.name || '') < (b.name || '') ? -1 : 1));

const removeWorkflowFromList = (workflows, slug) => workflows.filter(w => w.slug !== slug);

// The Auslöser currently set on a graph: the trigger node's `name` param, or null
// ('' / no trigger node = "kein Auslöser", never auto-fires). Drives the Testlauf
// context + the test-entity finder off the LIVE canvas, not the published trigger.
export const triggerOfGraph = graph => {
    const node = graph && (graph.nodes || []).find(n => n && n.type === 'trigger');
    const name = node && node.params && node.params.name;
    return name || null;
};

const resetTriggerFixtureIfChanged = (state, nextGraph) => {
    if (triggerOfGraph(state.graph) === triggerOfGraph(nextGraph)) return {};
    return {
        selectedTriggerEntity: null,
        triggerEntities: [],
        triggerEntitiesPageInfo: { page: 1, perPage: 24, hasMore: false },
        triggerEntitiesDegraded: false,
        triggerEntitiesDegradedMessage: null,
        triggerEntitiesLookup: null,
        triggerEntitiesRequestKey: null,
        fetchTriggerEntitiesInProgress: false,
        fetchTriggerEntitiesError: null,
        simulateError: null,
        manualRunError: null,
        ...NO_ACTIVE_SIMULATION,
    };
};

const adminSocialPostsPageReducer = (state = initialState, action = {}) => {
    const { type, payload } = action;
    switch (type) {
        case QUERY_POSTS_REQUEST:
            return {
                ...state,
                queryInProgress: true,
                queryError: null,
                queryKey: payload.queryKey,
            };
        case QUERY_POSTS_SUCCESS:
            if (payload.queryKey !== state.queryKey) return state;
            return {
                ...state,
                queryInProgress: false,
                posts: payload.posts,
                total: payload.total,
            };
        case QUERY_POSTS_ERROR:
            if (payload.queryKey !== state.queryKey) return state;
            return { ...state, queryInProgress: false, queryError: payload.error };

        case PENDING_COUNT_SUCCESS:
            return { ...state, pendingCount: payload };

        case FETCH_POST_REQUEST: {
            // A background publish poll may keep requesting post A after the route
            // has moved to post B. It still needs the returned value in its thunk,
            // but it must never retake ownership of the reviewer detail state.
            if (payload.background && String(payload.id) !== String(state.fetchPostTargetId)) {
                return state;
            }
            return {
                ...state,
                fetchPostInProgress: true,
                fetchPostError: null,
                fetchPostErrorStatus: null,
                fetchPostTargetId: payload.id,
                fetchPostRequestKey: payload.requestKey,
                // Opening a DIFFERENT post retires the previous post's error bar
                // immediately, not two seconds later when its detail happens to
                // arrive (and not never, if that detail fails to load). The red
                // banner describes an action on a post the operator has left;
                // riding it along onto a healthy one is the reviewer lying about
                // which post is broken. Re-reading the SAME post keeps it — that
                // is the "Erneut laden" button next to the message, and wiping
                // the message the click was about would be its own small lie.
                actionError:
                    String(payload.id) === String(state.fetchPostTargetId)
                        ? state.actionError
                        : null,
            };
        }
        case FETCH_POST_SUCCESS:
            if (
                payload.requestKey !== state.fetchPostRequestKey ||
                String(payload.id) !== String(state.fetchPostTargetId)
            ) {
                return state;
            }
            // A fresh server truth retires the previous action's error banner.
            // The publish poll refetches this post every 2.5s, so an error left
            // over from an earlier attempt would otherwise sit on screen next to
            // channels that have since succeeded — the banner outlived what it
            // described. Unconditional is correct because every error this
            // banner shows is raised BY an action and re-raised by the next one;
            // there is no error that is still true about a post we just re-read.
            return {
                ...state,
                fetchPostInProgress: false,
                selectedPost: payload.post,
                actionError: null,
            };
        case FETCH_POST_ERROR:
            if (
                payload.requestKey !== state.fetchPostRequestKey ||
                String(payload.id) !== String(state.fetchPostTargetId)
            ) {
                return state;
            }
            return {
                ...state,
                fetchPostInProgress: false,
                fetchPostError: payload.error,
                fetchPostErrorStatus: payload.status || null,
            };
        // The operator dismissed the error banner. It leaves the STORE, so it
        // cannot reappear on the next render or when the route comes back.
        case CLEAR_ACTION_ERROR:
            return { ...state, actionError: null };

        case CLEAR_SELECTED_POST:
            return {
                ...state,
                selectedPost: null,
                actionError: null,
                fetchPostInProgress: false,
                fetchPostError: null,
                fetchPostErrorStatus: null,
                fetchPostTargetId: null,
                fetchPostRequestKey: null,
                postPollRunId: null,
            };

        // ---- The post-poll lane ----
        // One lane, one owner: starting a poll claims it, and any later claim or
        // an explicit cancel evicts the previous owner, which then stops at its
        // next checkpoint. Because the identity is store state, the reviewer can
        // end a poll from an effect cleanup without reaching into module scope.
        case POST_POLL_START:
            return { ...state, postPollRunId: payload.runId };
        case POST_POLL_END:
            // A poll that has already been evicted must not clear its successor.
            if (payload.runId !== state.postPollRunId) return state;
            return { ...state, postPollRunId: null };
        case CANCEL_POST_POLL:
            return { ...state, postPollRunId: null };

        case POST_ACTION_REQUEST: {
            const optimistic = payload.optimisticPost;
            const selectedPost =
                optimistic &&
                state.selectedPost &&
                String(state.selectedPost.id) === String(payload.id)
                    ? optimistic
                    : state.selectedPost;
            return {
                ...state,
                actionInProgress: payload.id,
                actionError: null,
                selectedPost,
                posts: optimistic ? replaceInList(state.posts, optimistic) : state.posts,
            };
        }
        case POST_ACTION_SUCCESS: {
            const post = payload;
            const selectedPost =
                state.selectedPost && String(state.selectedPost.id) === String(post.id)
                    ? post
                    : state.selectedPost;
            return {
                ...state,
                actionInProgress: null,
                selectedPost,
                posts: replaceInList(state.posts, post),
            };
        }
        case POST_ACTION_ERROR: {
            // Roll the optimistic change back to the server-truth snapshot.
            const restore = payload.restorePost;
            const selectedPost =
                restore &&
                state.selectedPost &&
                String(state.selectedPost.id) === String(restore.id)
                    ? restore
                    : state.selectedPost;
            return {
                ...state,
                actionInProgress: null,
                actionError: payload.error,
                selectedPost,
                posts: restore ? replaceInList(state.posts, restore) : state.posts,
            };
        }

        case DELETE_POST_REQUEST: {
            const selectedPost =
                state.selectedPost && String(state.selectedPost.id) === String(payload.id)
                    ? null
                    : state.selectedPost;
            return {
                ...state,
                actionError: null,
                deletingPostIds: state.deletingPostIds.includes(payload.id)
                    ? state.deletingPostIds
                    : [...state.deletingPostIds, payload.id],
                selectedPost,
                posts: removeFromList(state.posts, payload.id),
                total: Math.max(0, state.total - 1),
            };
        }
        case DELETE_POST_SUCCESS:
            return {
                ...state,
                deletingPostIds: state.deletingPostIds.filter(
                    id => String(id) !== String(payload.id)
                ),
            };
        case DELETE_POST_ERROR: {
            const restore = payload.restorePost;
            const selectedPost =
                restore && payload.wasSelected && !state.selectedPost
                    ? restore
                    : state.selectedPost;
            return {
                ...state,
                actionError: payload.error,
                deletingPostIds: state.deletingPostIds.filter(
                    id => String(id) !== String(payload.id)
                ),
                selectedPost,
                posts: restoreToList(state.posts, restore),
                total: payload.restoreWasInList ? state.total + 1 : state.total,
            };
        }

        case FETCH_SETTINGS_REQUEST:
            return { ...state, fetchSettingsInProgress: true, fetchSettingsError: null };
        case FETCH_SETTINGS_SUCCESS:
            // Reset settingsSaved so a fresh load never shows a phantom "saved" tick
            // left over from a prior save in this session.
            return {
                ...state,
                fetchSettingsInProgress: false,
                settings: payload,
                settingsSaved: false,
            };
        case FETCH_SETTINGS_ERROR:
            return { ...state, fetchSettingsInProgress: false, fetchSettingsError: payload };

        case SAVE_SETTINGS_REQUEST:
            return {
                ...state,
                saveSettingsInProgress: true,
                saveSettingsError: null,
                settingsSaved: false,
            };
        case SAVE_SETTINGS_SUCCESS:
            return {
                ...state,
                saveSettingsInProgress: false,
                settings: payload,
                settingsSaved: true,
            };
        case SAVE_SETTINGS_ERROR:
            return { ...state, saveSettingsInProgress: false, saveSettingsError: payload };

        case FETCH_HEALTH_SUCCESS:
            return {
                ...state,
                health: payload.health,
                runtimeMode: payload.runtime,
                healthError: null,
            };
        case FETCH_HEALTH_ERROR:
            return { ...state, health: null, runtimeMode: null, healthError: payload };

        case FETCH_TRIGGERS_SUCCESS:
            return { ...state, triggers: payload };
        case FETCH_CATALOG_SUCCESS:
            return {
                ...state,
                catalogDefaults: (payload && payload.defaults) || {},
                catalogNodes: (payload && payload.nodes) || null,
            };
        case FETCH_CATALOG_ERROR:
            return state;

        case FETCH_WORKFLOW_REQUEST:
            return {
                ...state,
                workflowSlug: payload || state.workflowSlug,
                fetchWorkflowInProgress: true,
                fetchWorkflowError: null,
                // Loading a DIFFERENT workflow drops the prior test fixture — and
                // with it the Testlauf that was running against it, from the
                // first instant of the navigation rather than when its response
                // eventually lands.
                selectedTriggerEntity:
                    payload && payload !== state.workflowSlug ? null : state.selectedTriggerEntity,
                ...(payload && payload !== state.workflowSlug ? NO_ACTIVE_SIMULATION : null),
            };
        case DISCARD_DRAFT_SUCCESS:
        // Discarding a draft reverts the canvas to the published graph — the same
        // "new baseline, clear history" semantics as a load, so it shares the
        // branch below (and clears the discard flags).
        // falls through
        case PUBLISH_WORKFLOW_SUCCESS:
        // Publishing promotes the draft: the returned row carries the freshly
        // published graph with a cleared draft — reload the canvas from it.
        // falls through
        case FETCH_WORKFLOW_SUCCESS:
            // A fresh load resets the present graph and clears history — the
            // loaded graph is the new baseline (no undo across loads).
            return {
                ...state,
                fetchWorkflowInProgress: false,
                discardDraftInProgress: false,
                discardDraftError: null,
                publishInProgress: false,
                publishError: null,
                workflow: payload.workflow,
                workflows:
                    type !== FETCH_WORKFLOW_SUCCESS && payload.workflow
                        ? upsertWorkflowInList(state.workflows, payload.workflow)
                        : state.workflows,
                workflowSlug: (payload.workflow && payload.workflow.slug) || state.workflowSlug,
                graph: payload.graph,
                graphPast: [],
                graphFuture: [],
                workflowDirty: false,
                saveDraftError: null,
                selectedTriggerEntity:
                    payload.workflow &&
                    payload.workflow.slug &&
                    payload.workflow.slug !== state.workflowSlug
                        ? null
                        : state.selectedTriggerEntity,
                simulateError: null,
                manualRunError: null,
                // A load is a new baseline for the Testlauf too: whatever was
                // running described the graph that just got replaced.
                ...NO_ACTIVE_SIMULATION,
                ...resetTriggerFixtureIfChanged(state, payload.graph),
            };
        case FETCH_WORKFLOW_ERROR:
            return { ...state, fetchWorkflowInProgress: false, fetchWorkflowError: payload };

        case WORKFLOW_EDIT:
            // Snapshot the present onto past, set the new present, clear redo.
            return {
                ...state,
                graphPast: pushPast(state.graphPast, state.graph),
                graph: payload,
                graphFuture: [],
                workflowDirty: true,
                ...resetTriggerFixtureIfChanged(state, payload),
            };
        case WORKFLOW_LAYOUT_SYNC:
            // Layout edits (node coordinates / viewport) are saveable authoring
            // state, but they should not flood undo/redo while a manager pans or
            // drags the canvas.
            return {
                ...state,
                graph: payload,
                workflowDirty: true,
                ...resetTriggerFixtureIfChanged(state, payload),
            };
        case WORKFLOW_UNDO: {
            if (state.graphPast.length === 0) return state;
            const previous = state.graphPast[state.graphPast.length - 1];
            return {
                ...state,
                graph: previous,
                graphPast: state.graphPast.slice(0, -1),
                graphFuture: [state.graph, ...state.graphFuture],
                workflowDirty: true,
                ...resetTriggerFixtureIfChanged(state, previous),
            };
        }
        case WORKFLOW_REDO: {
            if (state.graphFuture.length === 0) return state;
            const next = state.graphFuture[0];
            return {
                ...state,
                graph: next,
                graphPast: pushPast(state.graphPast, state.graph),
                graphFuture: state.graphFuture.slice(1),
                workflowDirty: true,
                ...resetTriggerFixtureIfChanged(state, next),
            };
        }

        case SAVE_DRAFT_REQUEST:
            return {
                ...state,
                saveDraftInProgress: true,
                saveDraftError: null,
            };
        case SAVE_DRAFT_SUCCESS:
            // Reconcile the row metadata (its draftGraph is now non-null so the
            // "Entwurf" chip stays truthful) WITHOUT touching the live canvas graph
            // or clearing workflowDirty — the manager may have typed on since the
            // debounced save fired; the next debounce saves that.
            return {
                ...state,
                saveDraftInProgress: false,
                draftSavedAt: Date.now(),
                workflow: payload.workflow || state.workflow,
                workflows: payload.workflow
                    ? replaceWorkflowInList(state.workflows, payload.workflow)
                    : state.workflows,
            };
        case SAVE_DRAFT_ERROR:
            return { ...state, saveDraftInProgress: false, saveDraftError: payload };

        case PUBLISH_WORKFLOW_REQUEST:
            return { ...state, publishInProgress: true, publishError: null };
        // PUBLISH_WORKFLOW_SUCCESS is handled with FETCH_WORKFLOW_SUCCESS above.
        case PUBLISH_WORKFLOW_ERROR:
            return { ...state, publishInProgress: false, publishError: payload };

        case DISCARD_DRAFT_REQUEST:
            return { ...state, discardDraftInProgress: true, discardDraftError: null };
        // DISCARD_DRAFT_SUCCESS is handled with FETCH_WORKFLOW_SUCCESS above.
        case DISCARD_DRAFT_ERROR:
            return { ...state, discardDraftInProgress: false, discardDraftError: payload };

        case FETCH_WORKFLOWS_REQUEST:
            return { ...state, fetchWorkflowsInProgress: true, fetchWorkflowsError: null };
        case FETCH_WORKFLOWS_SUCCESS:
            return { ...state, fetchWorkflowsInProgress: false, workflows: payload };
        case FETCH_WORKFLOWS_ERROR:
            return { ...state, fetchWorkflowsInProgress: false, fetchWorkflowsError: payload };

        case SET_WORKFLOW_ENABLED_OPTIMISTIC:
            // Flip the row's `enabled` immediately and mark its slug in-flight
            // (the Switch shows pending) — design-for-instant.
            return {
                ...state,
                workflowsError: null,
                workflowsTogglingSlugs: state.workflowsTogglingSlugs.includes(payload.slug)
                    ? state.workflowsTogglingSlugs
                    : [...state.workflowsTogglingSlugs, payload.slug],
                workflows: setWorkflowEnabledInList(state.workflows, payload.slug, payload.enabled),
            };
        case SET_WORKFLOW_ENABLED_SUCCESS:
            return {
                ...state,
                workflowsTogglingSlugs: state.workflowsTogglingSlugs.filter(
                    s => s !== payload.slug
                ),
                // Reconcile from server truth (the returned workflow row).
                workflows: payload.workflow
                    ? replaceWorkflowInList(state.workflows, payload.workflow)
                    : state.workflows,
            };
        case SET_WORKFLOW_ENABLED_ERROR:
            // Roll the optimistic flip back to the prior value.
            return {
                ...state,
                workflowsError: payload.error,
                workflowsTogglingSlugs: state.workflowsTogglingSlugs.filter(
                    s => s !== payload.slug
                ),
                workflows: setWorkflowEnabledInList(
                    state.workflows,
                    payload.slug,
                    payload.previous
                ),
            };

        case RENAME_WORKFLOW_REQUEST:
            return {
                ...state,
                renameWorkflowError: null,
                workflowsRenamingSlugs: state.workflowsRenamingSlugs.includes(payload.slug)
                    ? state.workflowsRenamingSlugs
                    : [...state.workflowsRenamingSlugs, payload.slug],
            };
        case RENAME_WORKFLOW_SUCCESS:
            return {
                ...state,
                workflowsRenamingSlugs: state.workflowsRenamingSlugs.filter(
                    s => s !== payload.slug
                ),
                workflows: payload.workflow
                    ? replaceWorkflowInList(state.workflows, payload.workflow)
                    : state.workflows,
                workflow:
                    payload.workflow &&
                    state.workflow &&
                    state.workflow.slug === payload.workflow.slug
                        ? { ...state.workflow, ...payload.workflow }
                        : state.workflow,
            };
        case RENAME_WORKFLOW_ERROR:
            return {
                ...state,
                renameWorkflowError: payload.error,
                workflowsRenamingSlugs: state.workflowsRenamingSlugs.filter(
                    s => s !== payload.slug
                ),
            };

        case CREATE_WORKFLOW_REQUEST:
            return { ...state, createWorkflowInProgress: true, createWorkflowError: null };
        case CREATE_WORKFLOW_SUCCESS:
            // The new blank row joins the list (the caller navigates into it).
            return {
                ...state,
                createWorkflowInProgress: false,
                workflows: payload
                    ? upsertWorkflowInList(state.workflows, payload)
                    : state.workflows,
            };
        case CREATE_WORKFLOW_ERROR:
            return { ...state, createWorkflowInProgress: false, createWorkflowError: payload };

        case DUPLICATE_WORKFLOW_REQUEST:
            return { ...state, duplicateWorkflowInProgress: true, duplicateWorkflowError: null };
        case DUPLICATE_WORKFLOW_SUCCESS:
            return {
                ...state,
                duplicateWorkflowInProgress: false,
                workflows: payload
                    ? upsertWorkflowInList(state.workflows, payload)
                    : state.workflows,
            };
        case DUPLICATE_WORKFLOW_ERROR:
            return {
                ...state,
                duplicateWorkflowInProgress: false,
                duplicateWorkflowError: payload,
            };

        case DELETE_WORKFLOW_REQUEST:
            // Optimistic: drop the row immediately + mark its slug in-flight.
            return {
                ...state,
                deleteWorkflowError: null,
                deletingWorkflowSlugs: state.deletingWorkflowSlugs.includes(payload.slug)
                    ? state.deletingWorkflowSlugs
                    : [...state.deletingWorkflowSlugs, payload.slug],
                workflows: removeWorkflowFromList(state.workflows, payload.slug),
            };
        case DELETE_WORKFLOW_SUCCESS:
            return {
                ...state,
                deletingWorkflowSlugs: state.deletingWorkflowSlugs.filter(s => s !== payload.slug),
            };
        case DELETE_WORKFLOW_ERROR:
            // Restore the optimistically-removed row (409 posts-in-flight, etc.).
            return {
                ...state,
                deleteWorkflowError: payload.error,
                deletingWorkflowSlugs: state.deletingWorkflowSlugs.filter(s => s !== payload.slug),
                workflows: payload.restore
                    ? upsertWorkflowInList(state.workflows, payload.restore)
                    : state.workflows,
            };

        case FETCH_TRIGGER_LOG_REQUEST:
            return {
                ...state,
                triggerLogError: null,
                triggerLogLoadingSlugs: state.triggerLogLoadingSlugs.includes(payload)
                    ? state.triggerLogLoadingSlugs
                    : [...state.triggerLogLoadingSlugs, payload],
            };
        case FETCH_TRIGGER_LOG_SUCCESS:
            return {
                ...state,
                triggerLogBySlug: {
                    ...state.triggerLogBySlug,
                    [payload.slug]: payload.rows,
                },
                triggerLogLoadingSlugs: state.triggerLogLoadingSlugs.filter(
                    s => s !== payload.slug
                ),
            };
        case FETCH_TRIGGER_LOG_ERROR:
            return {
                ...state,
                triggerLogError: payload.error,
                triggerLogLoadingSlugs: state.triggerLogLoadingSlugs.filter(
                    s => s !== payload.slug
                ),
            };

        case FETCH_ACTIVE_RUN_REQUEST:
            return { ...state, fetchActiveRunInProgress: true };
        case FETCH_ACTIVE_RUN_SUCCESS:
            return {
                ...state,
                fetchActiveRunInProgress: false,
                activeRun: payload.post,
                // Why the server did not start a run holds only until that run moves:
                // once it was claimed, "another one is running" is no longer true.
                manualRunRefusal:
                    payload.post?.status === 'queued' && payload.post.id === state.activeRun?.id
                        ? state.manualRunRefusal
                        : null,
            };
        case FETCH_ACTIVE_RUN_ERROR:
            // Best-effort: a fetch failure just leaves the prior run (or null) —
            // the viz degrades to the last known state, never blocking the editor.
            return { ...state, fetchActiveRunInProgress: false };

        case SET_TEST_MODE:
            // Toggling test mode is mutually exclusive with the live run viz; the
            // sim result is dropped on exit (the fixture is kept for convenience).
            // Leaving Testmodus also ENDS the run: the flag used to survive here,
            // so the button read "Simuliere..." and stayed dead — with no cancel
            // and a 30-minute poll behind it — until that poll happened to finish.
            return {
                ...state,
                testMode: Boolean(payload),
                simulateError: null,
                ...NO_ACTIVE_SIMULATION,
            };
        case SET_TRIGGER_ENTITY:
            // Choosing a new entity invalidates previous run output in either mode
            // — including a run still in flight, whose result would describe the
            // fixture the operator just replaced.
            return {
                ...state,
                selectedTriggerEntity: payload || null,
                simulateError: null,
                manualRunError: null,
                ...NO_ACTIVE_SIMULATION,
            };
        case FETCH_TRIGGER_ENTITIES_REQUEST:
            return {
                ...state,
                fetchTriggerEntitiesInProgress: true,
                fetchTriggerEntitiesError: null,
                triggerEntitiesRequestKey: payload.requestKey,
                triggerEntities: payload.append ? state.triggerEntities : [],
                triggerEntitiesPageInfo: payload.append
                    ? state.triggerEntitiesPageInfo
                    : { page: 1, perPage: 24, hasMore: false },
                triggerEntitiesDegraded: payload.append ? state.triggerEntitiesDegraded : false,
                triggerEntitiesDegradedMessage: payload.append
                    ? state.triggerEntitiesDegradedMessage
                    : null,
                triggerEntitiesLookup: payload.append ? state.triggerEntitiesLookup : null,
            };
        case FETCH_TRIGGER_ENTITIES_SUCCESS:
            if (payload.requestKey !== state.triggerEntitiesRequestKey) return state;
            return {
                ...state,
                fetchTriggerEntitiesInProgress: false,
                triggerEntities: payload.append
                    ? [
                          ...state.triggerEntities,
                          ...payload.data.filter(
                              row => !state.triggerEntities.some(existing => existing.id === row.id)
                          ),
                      ]
                    : payload.data,
                triggerEntitiesSupported: payload.supported,
                triggerEntityLabels: payload.labels || state.triggerEntityLabels,
                triggerEntitiesPageInfo: payload.pageInfo,
                triggerEntitiesDegraded: payload.degraded,
                triggerEntitiesDegradedMessage: payload.degradedMessage,
                triggerEntitiesLookup: payload.append
                    ? state.triggerEntitiesLookup
                    : payload.lookup || null,
            };
        case FETCH_TRIGGER_ENTITIES_ERROR:
            if (payload.requestKey !== state.triggerEntitiesRequestKey) return state;
            return {
                ...state,
                fetchTriggerEntitiesInProgress: false,
                fetchTriggerEntitiesError: payload.error,
                triggerEntities: payload.append ? state.triggerEntities : [],
            };
        case SIMULATE_REQUEST:
            // Light the pipeline as "in flight" immediately (the generation nodes
            // pulse + connections glow via the existing isRunActive('generating')).
            return {
                ...state,
                simulateRunId: payload.runId,
                simulateStartedAt: payload.startedAt,
                simulateInProgress: true,
                simulateError: null,
                simRun: { status: 'generating', channels: [] },
            };
        case SIMULATE_SUCCESS:
            // Only the run that still owns the editor may paint a result. A
            // Testlauf started in workflow A and finished after the operator
            // moved to workflow B used to land in B's canvas as if it were B's.
            if (payload.runId !== state.simulateRunId) return state;
            return {
                ...state,
                ...NO_ACTIVE_SIMULATION,
                simRun: {
                    status: 'test_done',
                    channels: payload.channels || [],
                    trace: payload.trace || [],
                },
            };
        case SIMULATE_ERROR:
            if (payload.runId !== state.simulateRunId) return state;
            return {
                ...state,
                ...NO_ACTIVE_SIMULATION,
                simulateError: payload.error,
            };
        case SIMULATE_CANCEL:
            // The operator (or an implicit exit) ended the run. That is not an
            // error, so nothing red is left behind — just a live button again.
            return { ...state, simulateError: null, ...NO_ACTIVE_SIMULATION };
        case MANUAL_RUN_REQUEST:
            return {
                ...state,
                manualRunInProgress: true,
                manualRunError: null,
                manualRunRefusal: null,
            };
        case MANUAL_RUN_SUCCESS:
            return {
                ...state,
                manualRunInProgress: false,
                manualRunRefusal: payload.refusal || null,
                activeRun: payload.post,
            };
        case MANUAL_RUN_ERROR:
            return { ...state, manualRunInProgress: false, manualRunError: payload };

        case PRESETS_LOADED:
            return {
                ...state,
                designPresets: payload.presets,
                presetsLoaded: true,
            };
        case DESIGN_LOAD:
            // A fresh load seeds the present doc and clears history.
            return {
                ...state,
                designDoc: payload.doc,
                designPast: [],
                designFuture: [],
                designDirty: false,
                designSaved: false,
                saveDesignError: null,
            };
        case DESIGN_EDIT:
            return {
                ...state,
                designPast: pushPast(state.designPast, state.designDoc),
                designDoc: payload,
                designFuture: [],
                designDirty: true,
                designSaved: false,
            };
        case DESIGN_UNDO: {
            if (state.designPast.length === 0) return state;
            const previous = state.designPast[state.designPast.length - 1];
            return {
                ...state,
                designDoc: previous,
                designPast: state.designPast.slice(0, -1),
                designFuture: [state.designDoc, ...state.designFuture],
                designDirty: true,
                designSaved: false,
            };
        }
        case DESIGN_REDO: {
            if (state.designFuture.length === 0) return state;
            const next = state.designFuture[0];
            return {
                ...state,
                designDoc: next,
                designPast: pushPast(state.designPast, state.designDoc),
                designFuture: state.designFuture.slice(1),
                designDirty: true,
                designSaved: false,
            };
        }

        case SAVE_DESIGN_REQUEST:
            return {
                ...state,
                saveDesignInProgress: true,
                saveDesignError: null,
                designSaved: false,
            };
        case SAVE_DESIGN_SUCCESS:
            return {
                ...state,
                saveDesignInProgress: false,
                settings: payload.settings || state.settings,
                designPresets: payload.presets || state.designPresets,
                designDirty: false,
                designSaved: true,
            };
        case SAVE_DESIGN_ERROR:
            // Surface the rejection WITHOUT losing the manager's edits.
            return { ...state, saveDesignInProgress: false, saveDesignError: payload };

        default:
            return state;
    }
};

export default adminSocialPostsPageReducer;

// ================ Optimistic helpers ================ //

// Optimistic single-channel publish: flip ONLY the named channel to 'publishing'
// (applying an optional caption edit) and roll the post status up to 'publishing'
// so the reviewer frame shows its spinner immediately ("design for instant"). The
// server's per-channel publish path is authoritative and reconciles on success.
const optimisticPublishChannel = (post, platform, content, comment) => {
    if (!post) return post;
    const channels = (post.channels || []).map(c => {
        if (c.platform !== platform) return c;
        const next = { ...c, status: 'publishing', error: null };
        if (typeof content === 'string') next.content = content;
        if (typeof comment === 'string') next.comment = comment;
        return next;
    });
    const status = post.status === 'published' ? post.status : 'publishing';
    return { ...post, status, channels };
};

// ================ Action creators ================ //

export const queryPostsRequest = queryKey => ({ type: QUERY_POSTS_REQUEST, payload: { queryKey } });
export const queryPostsSuccess = (queryKey, posts, total) => ({
    type: QUERY_POSTS_SUCCESS,
    payload: { queryKey, posts, total },
});
export const queryPostsError = (queryKey, error) => ({
    type: QUERY_POSTS_ERROR,
    error: true,
    payload: { queryKey, error },
});

export const pendingCountSuccess = count => ({ type: PENDING_COUNT_SUCCESS, payload: count });

export const clearSelectedPost = () => ({ type: CLEAR_SELECTED_POST });

export const clearActionError = () => ({ type: CLEAR_ACTION_ERROR });

// Workflow editor sync action creators (pure store ops; no API).
export const editGraph = graph => ({ type: WORKFLOW_EDIT, payload: graph });
export const syncWorkflowLayout = graph => ({ type: WORKFLOW_LAYOUT_SYNC, payload: graph });
export const undoGraph = () => ({ type: WORKFLOW_UNDO });
export const redoGraph = () => ({ type: WORKFLOW_REDO });

// Design editor sync action creators (pure store ops; no API). The editor is
// authoritative; editDesign snapshots to the undo stack (design-for-instant).
export const editDesign = doc => ({ type: DESIGN_EDIT, payload: doc });
export const undoDesign = () => ({ type: DESIGN_UNDO });
export const redoDesign = () => ({ type: DESIGN_REDO });

// ================ Thunks ================ //

let queryPostsSeq = 0;
const DASHBOARD_POSTS_PAGE_SIZE = 100;

const responsePageCount = response => {
    const explicit = Number(response && response.totalPages);
    if (Number.isSafeInteger(explicit) && explicit >= 0) return explicit;
    const total = Number(response && response.total);
    return Number.isSafeInteger(total) && total > 0
        ? Math.ceil(total / DASHBOARD_POSTS_PAGE_SIZE)
        : 1;
};

// Hard ceiling per cohort fetch: 10 pages × 100 = 1 000 rows. With 90-day
// retention the working set stays far below this, so the cap is a runaway
// bound (a growing queue mid-fetch, a retention regression), not everyday
// truncation — without it the dashboard would chase pagination metadata
// through an arbitrarily large table on every load and 25 s poll tick.
const DASHBOARD_MAX_PAGES_PER_COHORT = 10;

/** Load one server-side status cohort completely, following growing page metadata. */
const loadAllPostPages = async (status, workflow) => {
    const rows = [];
    let page = 1;
    let lastPage = 1;
    do {
        // eslint-disable-next-line no-await-in-loop
        const response = await getSocialPosts(status, page, workflow, DASHBOARD_POSTS_PAGE_SIZE);
        rows.push(...((response && response.data) || []));
        // A queue can grow while pages are loading. Follow the largest page count
        // observed instead of freezing page one's metadata.
        lastPage = Math.max(lastPage, responsePageCount(response));
        page += 1;
    } while (page <= Math.min(lastPage, DASHBOARD_MAX_PAGES_PER_COHORT));
    return rows;
};

/** Later page snapshots win; offset shifts can repeat a row while the queue changes. */
const dedupeAndSortPosts = groups => {
    const byId = new Map();
    groups.forEach(rows =>
        (rows || []).forEach(row => {
            if (row && row.id != null) byId.set(String(row.id), row);
        })
    );
    return Array.from(byId.values()).sort(
        (a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt) ||
            Number(b.id) - Number(a.id) ||
            String(b.id).localeCompare(String(a.id))
    );
};

/**
 * Query posts for a tab. `statuses` is the list of post statuses the tab shows
 * ([] = all). `opts.workflow` scopes to ONE workflow by slug (the dashboard
 * filter). Multiple statuses are fetched in parallel and merged, newest first.
 */
export const queryPosts = (statuses = [], { workflow } = {}) => async dispatch => {
    const queryKey = `posts:${++queryPostsSeq}`;
    dispatch(queryPostsRequest(queryKey));
    try {
        const cohorts = statuses.length ? statuses : [undefined];
        const pages = await Promise.all(cohorts.map(status => loadAllPostPages(status, workflow)));
        const posts = dedupeAndSortPosts(pages);
        // Under concurrent inserts/deletes, offset pages can overlap or shift. The
        // only honest client total is the exact de-duplicated list it can display.
        dispatch(queryPostsSuccess(queryKey, posts, posts.length));
        return posts;
    } catch (e) {
        dispatch(queryPostsError(queryKey, e.message));
    }
};

const REVIEWER_QUEUE_PAGE_SIZE = 100;

/**
 * Load the complete human-review queue for sidebar/next-item navigation. The
 * repository filters Community-only manual rows before computing each page and
 * total. We follow the server's pagination metadata instead of silently stopping
 * at the first 20 items, then de-duplicate defensively if the queue changes while
 * the remaining pages are in flight.
 */
export const queryReviewerPosts = () => async dispatch => {
    const queryKey = `reviewer:${++queryPostsSeq}`;
    dispatch(queryPostsRequest(queryKey));
    try {
        const first = await getSocialReviewerQueue(1, REVIEWER_QUEUE_PAGE_SIZE);
        const totalPages = Math.max(0, Number(first.totalPages) || 0);
        const pages = [first];
        for (let page = 2; page <= totalPages; page += 1) {
            // Keep the admin API load bounded even if a neglected queue spans years.
            // eslint-disable-next-line no-await-in-loop
            pages.push(await getSocialReviewerQueue(page, REVIEWER_QUEUE_PAGE_SIZE));
        }
        const seen = new Set();
        const posts = pages
            .reduce((all, response) => all.concat(response.data || []), [])
            .filter(isReviewerQueuePost)
            .filter(post => {
                const key = String(post.id);
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .sort(
                (a, b) =>
                    new Date(b.createdAt) - new Date(a.createdAt) || Number(b.id) - Number(a.id)
            );
        const total = Number.isSafeInteger(Number(first.total))
            ? Number(first.total)
            : posts.length;
        dispatch(queryPostsSuccess(queryKey, posts, total));
        return posts;
    } catch (e) {
        dispatch(queryPostsError(queryKey, e.message));
        return undefined;
    }
};

export const fetchPendingCount = () => async dispatch => {
    try {
        const res = await getSocialReviewerQueue(1, 1);
        dispatch(pendingCountSuccess(res.total || 0));
    } catch (e) {
        // Badge is best-effort; never block the dashboard on it.
    }
};

/**
 * Refresh the exact REAL post the Studio started manually. The active run is
 * tracked by id so another listing event can never steal the graph overlay.
 */
export const fetchActiveRun = id => async dispatch => {
    if (!id) return null;
    dispatch({ type: FETCH_ACTIVE_RUN_REQUEST });
    try {
        const post = await getSocialPost(id);
        dispatch({
            type: FETCH_ACTIVE_RUN_SUCCESS,
            payload: { post, trigger: post && post.trigger },
        });
        return post;
    } catch (e) {
        dispatch({ type: FETCH_ACTIVE_RUN_ERROR, error: true, payload: e.message });
        return null;
    }
};

// ================ Test Mode ("Testmodus") ================ //

// Enter/leave the sandbox. Leaving drops the sim result (reducer).
export const setTestMode = on => ({ type: SET_TEST_MODE, payload: on });

// Pick (or clear) the trigger entity the Auslöser node fires with.
export const setTriggerEntity = entity => ({ type: SET_TRIGGER_ENTITY, payload: entity });

/**
 * Load the finder's candidate entities for the active trigger. Best-effort — a
 * failure shows the finder's error state, never blocks the editor.
 */
let triggerEntitiesRequestSequence = 0;

export const fetchTriggerEntities = (
    trigger,
    q = '',
    page = 1,
    append = false
) => async dispatch => {
    const requestKey = ++triggerEntitiesRequestSequence;
    const pageNumber = Math.max(Number(page) || 1, 1);
    dispatch({
        type: FETCH_TRIGGER_ENTITIES_REQUEST,
        payload: { requestKey, trigger, q, page: pageNumber, append: Boolean(append) },
    });
    try {
        const res = await getSocialTriggerEntities(trigger, q, pageNumber);
        dispatch({
            type: FETCH_TRIGGER_ENTITIES_SUCCESS,
            payload: {
                requestKey,
                append: Boolean(append),
                data: (res && res.data) || [],
                supported: res ? res.supported !== false : true,
                pageInfo: (res && res.pageInfo) || {
                    page: pageNumber,
                    perPage: 24,
                    hasMore: false,
                },
                degraded: Boolean(res && res.degraded),
                degradedMessage: (res && res.degradedMessage) || null,
                lookup: (res && res.lookup) || null,
                labels: {
                    selectorLabel: (res && res.selectorLabel) || 'Eintrag',
                    selectorPlaceholder: (res && res.selectorPlaceholder) || 'Eintrag suchen...',
                    selectorScope: (res && res.selectorScope) || '',
                    unsupportedMessage:
                        (res && res.unsupportedMessage) ||
                        'Für diesen Auslöser gibt es noch keine Daten-Quelle.',
                    emptyMessage: (res && res.emptyMessage) || 'Keine passenden Einträge gefunden.',
                },
            },
        });
        return res;
    } catch (e) {
        dispatch({
            type: FETCH_TRIGGER_ENTITIES_ERROR,
            error: true,
            payload: { requestKey, append: Boolean(append), error: e.message },
        });
        return null;
    }
};

// ================ Cancellable, backing-off polls ================ //
//
// Three things here follow a server-side job by polling: the Testlauf, a publish
// and a regenerate. Each used to be a bare
// `while (Date.now() < deadline) { await sleep(2500); ... }` with no way to stop
// it — unmounting the reviewer, opening another post or leaving Testmodus left
// the loop running, and loops could stack. Measured on production that meant up
// to 48 full-post fetches of a 6 MB document per publish, which saturates the
// browser's per-host connection pool: "das Tool hängt sich immer wieder auf".
//
// So there is ONE mechanism, and all three use it:
//
//   * RUN IDENTITY lives in the STORE (`postPollRunId`, `simulateRunId`), not in
//     a module variable. A loop re-reads it after every await and stops the
//     instant it is no longer the owner. Because it is store state, any reducer
//     can end a poll — which is how "Testmodus aus" and "reviewer unmounted"
//     cancel one without a component reaching into module scope, and how a
//     stale terminal action gets ignored instead of overwriting live state.
//   * BACKOFF: the first ticks are fast (most work settles in seconds), then the
//     interval grows geometrically to a cap. A publish now costs ~12 requests
//     over its two minutes instead of 48; a Testlauf ~120 instead of 720.
//
// WHAT IS STILL WRONG, and is not fixable from this file: both lanes poll the
// FULL document. The right shape is a lightweight status projection —
// `GET /api/social-posts/:id/status` answering
// `{ status, updatedAt, channels: [{ platform, status, postizOutcome }] }` — and
// the same for the Testlauf job. Backoff plus stopping the moment the channels
// settle is the honest interim; it cuts the request COUNT, not the per-request
// size. (The content-addressed image store landing alongside this cuts the size.)
const POLL_FIRST_DELAY_MS = 1500;
const POLL_MAX_DELAY_MS = 15000;
const POLL_BACKOFF = 1.5;

/**
 * The next poll interval: geometric growth, capped. Pure so the cadence is a
 * tested property and not something you have to run a stopwatch against.
 * @param {number} delayMs the interval just used
 * @returns {number} the next interval in ms
 */
export const nextPollDelay = delayMs =>
    Math.min(Math.round(delayMs * POLL_BACKOFF), POLL_MAX_DELAY_MS);

/**
 * Every delay a poll of this length would use, in order — the cadence made
 * inspectable, so "how many requests does one publish cost?" is a test and not
 * an estimate.
 * @param {number} timeoutMs
 * @returns {number[]}
 */
export const pollDelaySchedule = timeoutMs => {
    const delays = [];
    let elapsed = 0;
    let delay = POLL_FIRST_DELAY_MS;
    while (elapsed + delay <= timeoutMs) {
        delays.push(delay);
        elapsed += delay;
        delay = nextPollDelay(delay);
    }
    return delays;
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// Monotonic ids for both lanes. Shared so an id is never reused across lanes,
// which keeps a run identity meaningful in a log line.
let pollRunSequence = 0;
const nextPollRunId = () => {
    pollRunSequence += 1;
    return pollRunSequence;
};

/**
 * Stop whatever is following a post. The reviewer fires this when the open post
 * changes and when it unmounts, so a publish or regenerate started on the post
 * the operator has left cannot keep fetching it (nor keep several such loops
 * alive at once). It never cancels the SERVER's work — publishing continues and
 * the next load shows its real outcome; it only stops us watching.
 * @returns {Object} redux action
 */
export const cancelPostPolls = () => ({ type: CANCEL_POST_POLL });

/**
 * Follow ONE post until `isDone(post)` or the deadline. Runs on the shared
 * post-poll lane: starting another post poll, or `cancelPostPolls()`, evicts
 * this one, which then stops at its next checkpoint without dispatching
 * anything further.
 *
 * Resolves to the freshest post it saw — including when it was cancelled, so a
 * caller always gets the last known truth rather than `undefined` — plus which
 * of the three endings happened.
 *
 * @param {string|number} id
 * @param {{timeoutMs:number, isDone:function, initial?:?Object}} options
 * @returns {Promise<{post:?Object, cancelled:boolean, timedOut:boolean}>}
 */
const pollPost = (id, { timeoutMs, isDone, initial = null }) => async (dispatch, getState) => {
    const runId = nextPollRunId();
    dispatch({ type: POST_POLL_START, payload: { runId } });
    const owns = () => getState().AdminSocialPostsPage.postPollRunId === runId;
    const deadline = Date.now() + timeoutMs;
    let post = initial;
    let delay = POLL_FIRST_DELAY_MS;
    while (Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(delay);
        // Checked on BOTH sides of the request: cancellation can land while a
        // fetch is in flight, and a cancelled poll must not write its answer.
        if (!owns()) return { post, cancelled: true, timedOut: false };
        // eslint-disable-next-line no-await-in-loop
        post = (await dispatch(fetchPost(id, { background: true }))) || post;
        if (!owns()) return { post, cancelled: true, timedOut: false };
        if (isDone(post)) {
            dispatch({ type: POST_POLL_END, payload: { runId } });
            return { post, cancelled: false, timedOut: false };
        }
        delay = nextPollDelay(delay);
    }
    dispatch({ type: POST_POLL_END, payload: { runId } });
    return { post, cancelled: false, timedOut: true };
};

/**
 * Run the live (even unsaved) canvas graph against the chosen fixture and store
 * the produced channels as `simRun` — ZERO side effects (the server never writes a
 * post or sends mail). Reads the CURRENT present graph + fixture from the store so
 * a Testlauf always reflects the latest edits.
 */
// The Testlauf is a server-side BACKGROUND JOB: it runs with the exact budgets of
// a real trigger run (an LLM attempt may take minutes), so start returns 202 +
// jobId and we POLL until the job settles. The poll deadline mirrors the server's
// job TTL (SIMULATE_JOB_TTL_MS in server/routes/socialMedia.js); hitting it means
// something is genuinely wedged, and the message says so honestly. The deadline is
// long ON PURPOSE — abandoning a run that is still working would be worse — which
// is exactly why it must be cancellable: `cancelSimulation()` (and every implicit
// exit) hands the operator back their button in one click instead of thirty
// minutes.
const SIMULATE_POLL_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * End the active Testlauf from the operator's side. The server job keeps running
 * (a simulation has no side effects, and there is no server cancel API), but
 * nothing is following it any more and the editor is usable again immediately.
 * @returns {Object} redux action
 */
export const cancelSimulation = () => ({ type: SIMULATE_CANCEL });

export const runSimulation = () => async (dispatch, getState) => {
    const { graph, selectedTriggerEntity } = getState().AdminSocialPostsPage;
    if (!graph || !selectedTriggerEntity || !selectedTriggerEntity.id) return null;
    // The Testlauf fires the Auslöser CURRENTLY set on the canvas graph (the trigger
    // node's `name`), not the workflow's last-published trigger.
    const trigger = triggerOfGraph(graph);
    if (!trigger) return null;
    const runId = nextPollRunId();
    dispatch({ type: SIMULATE_REQUEST, payload: { runId, startedAt: Date.now() } });
    const owns = () => getState().AdminSocialPostsPage.simulateRunId === runId;
    try {
        const { jobId } = await simulateSocialWorkflow({
            trigger,
            graph,
            entityId: selectedTriggerEntity.id,
        });
        if (!owns()) return null;
        const deadline = Date.now() + SIMULATE_POLL_TIMEOUT_MS;
        let delay = POLL_FIRST_DELAY_MS;
        while (Date.now() < deadline) {
            // eslint-disable-next-line no-await-in-loop
            await sleep(delay);
            if (!owns()) return null;
            // eslint-disable-next-line no-await-in-loop
            const job = await getSocialSimulation(jobId);
            if (!owns()) return null;
            if (job.status === 'done') {
                dispatch({
                    type: SIMULATE_SUCCESS,
                    payload: { runId, channels: job.channels || [], trace: job.trace || [] },
                });
                return job;
            }
            if (job.status === 'failed') {
                throw new Error(job.message || 'Testlauf fehlgeschlagen.');
            }
            delay = nextPollDelay(delay);
        }
        throw new Error('Der Testlauf läuft ungewöhnlich lange — bitte später erneut versuchen.');
    } catch (e) {
        if (!owns()) return null;
        dispatch({ type: SIMULATE_ERROR, error: true, payload: { runId, error: e.message } });
        return null;
    }
};

/**
 * Start a REAL production run for the selected entity. Reads the saved workflow on
 * the server side; the editor disables this action while the canvas is dirty.
 */
export const runManualProduction = () => async (dispatch, getState) => {
    const { workflowSlug, selectedTriggerEntity } = getState().AdminSocialPostsPage;
    if (!workflowSlug || !selectedTriggerEntity || !selectedTriggerEntity.id) return null;
    dispatch({ type: MANUAL_RUN_REQUEST });
    try {
        const res = await runManualSocialWorkflow({
            slug: workflowSlug,
            entityId: selectedTriggerEntity.id,
        });
        dispatch({
            type: MANUAL_RUN_SUCCESS,
            payload: {
                post: res && res.post ? res.post : null,
                refusal: res && res.started === false ? res.reason || null : null,
            },
        });
        dispatch(fetchPendingCount());
        return res && res.post ? res.post : null;
    } catch (e) {
        dispatch({ type: MANUAL_RUN_ERROR, error: true, payload: e.message });
        return null;
    }
};

let fetchPostRequestSequence = 0;

export const fetchPost = (id, { background = false } = {}) => async dispatch => {
    const requestKey = `post:${++fetchPostRequestSequence}`;
    dispatch({
        type: FETCH_POST_REQUEST,
        payload: { id, requestKey, background: Boolean(background) },
    });
    try {
        const post = await getSocialPost(id);
        dispatch({ type: FETCH_POST_SUCCESS, payload: { id, requestKey, post } });
        return post;
    } catch (e) {
        dispatch({
            type: FETCH_POST_ERROR,
            error: true,
            payload: { id, requestKey, error: e.message, status: e.status || null },
        });
    }
};

const runPostAction = (id, optimisticPost, apiCall) => async (dispatch, getState) => {
    const { selectedPost } = getState().AdminSocialPostsPage;
    dispatch({ type: POST_ACTION_REQUEST, payload: { id, optimisticPost } });
    try {
        const post = await apiCall();
        dispatch({ type: POST_ACTION_SUCCESS, payload: post });
        dispatch(fetchPendingCount());
        return post;
    } catch (e) {
        dispatch({
            type: POST_ACTION_ERROR,
            payload: { error: e.message, restorePost: selectedPost },
        });
        throw e;
    }
};

export const regeneratePost = id => (dispatch, getState) => {
    const { selectedPost } = getState().AdminSocialPostsPage;
    const optimistic = selectedPost ? { ...selectedPost, status: 'queued', channels: [] } : null;
    return dispatch(runPostAction(id, optimistic, () => regenerateSocialPost(id)));
};

export const cancelPost = id => (dispatch, getState) => {
    const { selectedPost } = getState().AdminSocialPostsPage;
    const optimistic = selectedPost ? { ...selectedPost, status: 'cancelled' } : null;
    return dispatch(runPostAction(id, optimistic, () => cancelSocialPost(id)));
};

export const resolvePostizOutcome = (id, platform, resolution) => (dispatch, getState) => {
    const { selectedPost } = getState().AdminSocialPostsPage;
    return dispatch(
        runPostAction(id, selectedPost, () => resolveSocialPostizOutcome(id, platform, resolution))
    );
};

export const deletePost = id => async (dispatch, getState) => {
    const { posts, selectedPost } = getState().AdminSocialPostsPage;
    const restorePost =
        (posts || []).find(p => String(p.id) === String(id)) ||
        (selectedPost && String(selectedPost.id) === String(id) ? selectedPost : null);
    const restoreWasInList = (posts || []).some(p => String(p.id) === String(id));
    const wasSelected = Boolean(selectedPost && String(selectedPost.id) === String(id));
    dispatch({ type: DELETE_POST_REQUEST, payload: { id } });
    try {
        const result = await deleteSocialPost(id);
        dispatch({ type: DELETE_POST_SUCCESS, payload: { id, result } });
        dispatch(fetchPendingCount());
        return result;
    } catch (e) {
        dispatch({
            type: DELETE_POST_ERROR,
            payload: { id, error: e.message, restorePost, restoreWasInList, wasSelected },
        });
        throw e;
    }
};

// A channel whose current reviewer action is complete. Manual Community work has
// moved to the extension, while a held Story has become its own later dedicated
// action. Failures are deliberately absent: they keep the reviewer on this post.
export const isReviewerChannelHandled = ch =>
    ['published', 'skipped'].includes(ch.status) ||
    Boolean(ch.manualReleasedAt) ||
    ch.heldForManual === true;

/**
 * A requested channel this publish poll no longer waits on. A failed provider
 * attempt is terminal for polling even though it is not handled for reviewer nav.
 *
 * The one exception is the publish spine's PRE-CREATE FENCE: immediately before
 * calling Postiz the server persists the channel as `failed` with
 * `postizOutcome:'unknown'`, so a crash at any later instruction can only
 * produce a reconciliation task and never a duplicate post. Read literally that
 * fence looks settled — which is why the poll used to stop mid-flight and the
 * reviewer showed a red error for a publish that succeeded seconds later. While
 * the POST is still `publishing` the fence is an in-flight marker, not a verdict,
 * so keep polling until the post leaves that status; by then the fence has been
 * overwritten by the real outcome or is genuinely final.
 *
 * `accepted` is terminal FOR THIS POLL. Postiz answers QUEUE for every healthy
 * live publish and the spine records that as `accepted` — which IS the answer to
 * the question this poll asks ("did my click reach the provider?"). The
 * platform's own confirmation arrives from the reconcile cron minutes later,
 * well past this poll's two-minute deadline, so polling through it only ever
 * burned the full 48 fetches to learn nothing while "Wird veröffentlicht…" span
 * over a publish that had already succeeded. The reviewer follows that
 * confirmation separately, gently, and says out loud that it is waiting.
 */
export const isSettledChannel = (ch, post) => {
    if (ch.postizOutcome === 'unknown' && post && post.status === 'publishing') return false;
    if (ch.status === 'accepted' && ch.postizOutcome === 'accepted') return true;
    return ch.status === 'failed' || isReviewerChannelHandled(ch);
};

const PUBLISH_POLL_TIMEOUT_MS = 120000;

/**
 * The ONE client publish pillar. The server route only MARKS the requested
 * channels and answers 202 instantly (external Postiz/Storrito I/O runs in the
 * background — running it inside the request used to 504 on DigitalOcean's ~30s
 * gateway while the publish silently kept going). So this thunk: optimistic flip
 * -> one request -> POLL the post until every requested channel settles.
 * fetchPost updates the store each tick, so the reviewer watches channels flip
 * live. Resolves to the reconciled post; on poll timeout it resolves to the
 * latest snapshot WITHOUT erroring — publishing continues server-side, the
 * status pills stay honest, and a later refresh shows the outcome. Cancelling
 * (leaving the post, leaving the reviewer) resolves it the same way: the last
 * snapshot, no error, and the server keeps publishing.
 * `requests` = [{ platform, content?, comment? }].
 */
const requestPublish = (id, requests, options = {}) => async (dispatch, getState) => {
    const { selectedPost } = getState().AdminSocialPostsPage;
    const optimistic = requests.reduce(
        (acc, r) => optimisticPublishChannel(acc, r.platform, r.content, r.comment),
        selectedPost
    );
    const marked = await dispatch(
        runPostAction(id, optimistic, () => requestSocialPublish(id, requests, options))
    );

    const platforms = requests.map(r => r.platform);
    const settled = post => {
        const channels = (post && post.channels) || [];
        return platforms.every(p => {
            const ch = channels.find(c => c.platform === p);
            return !ch || isSettledChannel(ch, post);
        });
    };
    if (settled(marked)) return marked;

    const { post } = await dispatch(
        pollPost(id, {
            timeoutMs: PUBLISH_POLL_TIMEOUT_MS,
            isDone: settled,
            initial: marked,
        })
    );
    dispatch(fetchPendingCount());
    return post;
};

// Regeneration re-runs the workflow: the post drops to `queued`, the server
// re-generates, and the fresh drafts appear. Ninety seconds is the observed
// worst case for an LLM + render pass.
const REGENERATE_POLL_TIMEOUT_MS = 90000;
const GENERATING_STATUSES = ['queued', 'generating'];

/**
 * "Neu generieren": start the re-run, then follow the post until the fresh
 * drafts exist.
 *
 * The follow happens ONLY AFTER A CONFIRMED START. The reviewer used to own this
 * loop and awaited a dispatch whose rejection had already been swallowed at the
 * connect boundary, so it could not tell a failed start from a successful one —
 * a regenerate that 409'd still polled the post 36 times over 90 seconds behind
 * a request that never happened. Here the start is awaited inside the thunk: it
 * rejects, and the poll below is simply never reached.
 *
 * @param {string|number} id
 * @returns {Promise<?Object>} the post once generation settled (or the last seen)
 */
export const regeneratePostAndFollow = id => async dispatch => {
    await dispatch(regeneratePost(id));
    const { post } = await dispatch(
        pollPost(id, {
            timeoutMs: REGENERATE_POLL_TIMEOUT_MS,
            isDone: p => Boolean(p) && !GENERATING_STATUSES.includes(p.status),
        })
    );
    return post;
};

// The server's reconcile cron runs every three minutes. Follow an accepted
// channel across roughly three of its passes and then STOP: an endless poll
// would be the same connection-pool mistake in slow motion, and by then the
// reviewer has an honest "that is taking longer than usual" message with a
// manual reload and a link into Postiz.
const POSTIZ_CONFIRMATION_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Watch a post whose channel Postiz has accepted until the platform's
 * confirmation lands. This is what turns the reviewer's most confusing state —
 * caption read-only, publish button gone, every other button answering 409 —
 * into a visible pending state that clears itself while the operator watches.
 *
 * Uses the shared post-poll lane, so opening another post or leaving the
 * reviewer ends it like any other poll.
 *
 * @param {string|number} id
 * @returns {Promise<?Object>} the post once nothing is awaiting confirmation
 */
export const followPostizConfirmation = id => async dispatch => {
    const { post } = await dispatch(
        pollPost(id, {
            timeoutMs: POSTIZ_CONFIRMATION_POLL_TIMEOUT_MS,
            isDone: p => Boolean(p) && !postAwaitsPostizConfirmation(p),
        })
    );
    return post;
};

/**
 * Publish JUST ONE channel (the reviewer's per-platform Posten): a thin wrapper
 * over requestPublish carrying the optional inline caption / first-comment edit.
 */
export const publishChannel = (id, platform, content, comment) => {
    const request = { platform };
    if (typeof content === 'string') request.content = content;
    if (typeof comment === 'string') request.comment = comment;
    return requestPublish(id, [request], { dedicatedChannelAction: true });
};

/**
 * Publish ALL enabled, not-yet-published channels (the reviewer's "Alle posten"):
 * ONE request for every open channel (the server marks them atomically), then the
 * shared poll. `captionEdits` / `commentEdits` are optional { platform: text }
 * maps carrying the reviewer's inline edits through the same request.
 */
export const postAll = (id, captionEdits = {}, commentEdits = {}) => (dispatch, getState) => {
    const { selectedPost } = getState().AdminSocialPostsPage;
    const targets = ((selectedPost && selectedPost.channels) || [])
        .filter(isBulkPublishableChannel)
        .map(c => c.platform);
    if (!targets.length) return dispatch(fetchPost(id));

    const requests = targets.map(platform => {
        const request = { platform };
        if (typeof captionEdits[platform] === 'string') request.content = captionEdits[platform];
        if (typeof commentEdits[platform] === 'string') request.comment = commentEdits[platform];
        return request;
    });
    return dispatch(requestPublish(id, requests));
};

/**
 * Advance the reviewer to the NEXT pending post after the current one. Pure
 * client navigation built on the loaded pending list (the reviewer keeps it in
 * sync); returns the next post id or null when none remain (all-caught-up). The
 * component owns the actual route push so navigation stays in the view layer.
 */
export const nextPendingPostId = currentId => (dispatch, getState) => {
    const { posts } = getState().AdminSocialPostsPage;
    const pending = (posts || []).filter(
        p => isReviewerQueuePost(p) && String(p.id) !== String(currentId)
    );
    return pending.length ? pending[0].id : null;
};

export const fetchSettings = () => async dispatch => {
    dispatch({ type: FETCH_SETTINGS_REQUEST });
    try {
        const settings = await getSocialSettings();
        dispatch({ type: FETCH_SETTINGS_SUCCESS, payload: settings });
        return settings;
    } catch (e) {
        dispatch({ type: FETCH_SETTINGS_ERROR, error: true, payload: e.message });
    }
};

export const saveSettings = config => async dispatch => {
    dispatch({ type: SAVE_SETTINGS_REQUEST });
    try {
        const settings = await updateSocialSettings(config);
        dispatch({ type: SAVE_SETTINGS_SUCCESS, payload: settings });
        return settings;
    } catch (e) {
        dispatch({ type: SAVE_SETTINGS_ERROR, error: true, payload: e.message });
        throw e;
    }
};

export const fetchHealth = () => async dispatch => {
    try {
        const res = await getSocialHealth();
        dispatch({
            type: FETCH_HEALTH_SUCCESS,
            payload: { health: res.data || [], runtime: res.runtime || null },
        });
    } catch (e) {
        dispatch({ type: FETCH_HEALTH_ERROR, error: true, payload: e.message });
    }
};

// The browser-extension API key is display-only (shown in Settings, pasted into
// the extension), so these thunks just return the key — the form holds it in
// local state rather than the store.
export const fetchExtensionKey = () => async () => {
    const res = await getSocialExtensionKey();
    return res.apiKey || '';
};

export const rotateExtensionKey = () => async () => {
    const res = await rotateSocialExtensionKey();
    return res.apiKey || '';
};

// The connected Storrito IG accounts populate the Settings account picker. Like
// the extension key, this is display-only helper data the form holds in local
// state, so the thunk just returns the list. The picker falls back to free-text
// when it is empty / unavailable, so a fault degrades to [] (never traps the
// operator) — the connection error itself is surfaced by the health row.
export const fetchStorritoAccounts = () => async () => {
    const res = await getSocialStorritoAccounts();
    return (res && res.accounts) || [];
};

// ================ Workflow editor thunks ================ //

export const fetchTriggers = () => async dispatch => {
    try {
        const res = await getSocialTriggers();
        dispatch({ type: FETCH_TRIGGERS_SUCCESS, payload: res.data || [] });
    } catch (e) {
        // The picker is best-effort; the editor still works for the default
        // trigger without the registry list.
    }
};

export const fetchCatalog = () => async dispatch => {
    try {
        const res = await getSocialCatalog();
        dispatch({ type: FETCH_CATALOG_SUCCESS, payload: res || {} });
        return res;
    } catch (e) {
        dispatch({ type: FETCH_CATALOG_ERROR, error: true, payload: e.message });
        return null;
    }
};

const EMPTY_GRAPH = { version: 1, nodes: [], edges: [] };

// The graph the studio canvas loads: the unpublished DRAFT when one exists,
// otherwise the published graph. So reopening a workflow with in-flight edits
// resumes exactly where the manager left off.
const canvasGraphOf = workflow =>
    (workflow && (workflow.draftGraph || workflow.graph)) || EMPTY_GRAPH;

/**
 * Load one workflow by its permanent 4-letter slug — the studio loader. The
 * canvas opens on the draft if there is one, else the published graph.
 */
export const fetchWorkflowBySlug = slug => async dispatch => {
    dispatch({ type: FETCH_WORKFLOW_REQUEST, payload: slug });
    try {
        const res = await getSocialWorkflowBySlug(slug);
        const workflow = res.workflow || {};
        dispatch({
            type: FETCH_WORKFLOW_SUCCESS,
            payload: { workflow, graph: canvasGraphOf(workflow) },
        });
        return workflow;
    } catch (e) {
        dispatch({ type: FETCH_WORKFLOW_ERROR, error: true, payload: e.message });
    }
};

/**
 * Autosave the present graph as this workflow's DRAFT (one PUT; the studio owns
 * the debounce). Shape-checked only server-side — publishing is the strict gate.
 * Never validates strictly, so a mid-edit graph saves without noise.
 */
export const saveDraft = ({ force = false } = {}) => async (dispatch, getState) => {
    const state = getState().AdminSocialPostsPage;
    const { workflowSlug, graph } = state;
    if (!workflowSlug || !graph) return null;
    // A debounce timer that fires while a publish is in flight would re-create
    // the draft AFTER publish cleared it (a phantom "Entwurf" chip). The publish
    // thunk flushes explicitly with force:true; every other caller yields to it.
    if (!force && state.publishInProgress) return null;
    dispatch({ type: SAVE_DRAFT_REQUEST });
    try {
        const res = await saveSocialWorkflowDraft(workflowSlug, graph);
        dispatch({ type: SAVE_DRAFT_SUCCESS, payload: { workflow: res.workflow } });
        return res.workflow;
    } catch (e) {
        dispatch({ type: SAVE_DRAFT_ERROR, error: true, payload: e.message });
        return null;
    }
};

/**
 * Publish the draft — THE strict validate gate. Flushes the current canvas as the
 * draft first (so the latest edits, possibly not yet debounced-saved, are what we
 * publish), then POSTs publish. On 400 the German message surfaces as publishError
 * without losing the canvas. On success PUBLISH_WORKFLOW_SUCCESS reloads the canvas
 * from the freshly published graph (draft cleared).
 */
export const publishWorkflow = () => async (dispatch, getState) => {
    const { workflowSlug } = getState().AdminSocialPostsPage;
    if (!workflowSlug) return null;
    dispatch({ type: PUBLISH_WORKFLOW_REQUEST });
    try {
        await dispatch(saveDraft({ force: true }));
        const res = await publishSocialWorkflow(workflowSlug);
        const workflow = res.workflow || {};
        dispatch({
            type: PUBLISH_WORKFLOW_SUCCESS,
            payload: { workflow, graph: canvasGraphOf(workflow) },
        });
        return workflow;
    } catch (e) {
        dispatch({ type: PUBLISH_WORKFLOW_ERROR, error: true, payload: e.message });
        throw e;
    }
};

/**
 * "Zurücksetzen": discard the draft — the canvas reverts to the published graph.
 * DISCARD_DRAFT_SUCCESS reuses the FETCH_WORKFLOW_SUCCESS reducer branch so the
 * graph, undo history, and dirty flag all reset in one path.
 */
export const discardDraft = () => async (dispatch, getState) => {
    const { workflowSlug } = getState().AdminSocialPostsPage;
    if (!workflowSlug) return null;
    dispatch({ type: DISCARD_DRAFT_REQUEST });
    try {
        const res = await discardSocialWorkflowDraft(workflowSlug);
        const workflow = res.workflow || {};
        dispatch({
            type: DISCARD_DRAFT_SUCCESS,
            payload: { workflow, graph: canvasGraphOf(workflow) },
        });
        return workflow;
    } catch (e) {
        dispatch({ type: DISCARD_DRAFT_ERROR, error: true, payload: e.message });
        throw e;
    }
};

// ================ Workflow LIST thunks (P4 fullscreen tab) ================ //

/**
 * Load every trigger's active workflow — the Workflow-tab list. One row per
 * trigger (one active workflow per trigger). Client-only (admin-cookie auth).
 */
export const fetchWorkflows = () => async dispatch => {
    dispatch({ type: FETCH_WORKFLOWS_REQUEST });
    try {
        const res = await getSocialWorkflows();
        dispatch({ type: FETCH_WORKFLOWS_SUCCESS, payload: res.data || [] });
        return res.data || [];
    } catch (e) {
        dispatch({ type: FETCH_WORKFLOWS_ERROR, error: true, payload: e.message });
    }
};

/**
 * Toggle a workflow on/off (by slug). OPTIMISTIC (design-for-instant): the row
 * flips immediately, reconciles from the server's returned row, and rolls back to
 * the prior value on error. `previous` is captured BEFORE the optimistic flip so a
 * rejection restores exactly what was there.
 */
export const setWorkflowEnabled = (slug, enabled) => async (dispatch, getState) => {
    const before = getState().AdminSocialPostsPage.workflows.find(w => w.slug === slug);
    const previous = before ? before.enabled : !enabled;
    dispatch({ type: SET_WORKFLOW_ENABLED_OPTIMISTIC, payload: { slug, enabled } });
    try {
        const res = await setSocialWorkflowEnabled(slug, enabled);
        dispatch({
            type: SET_WORKFLOW_ENABLED_SUCCESS,
            payload: { slug, workflow: res.workflow },
        });
        return res.workflow;
    } catch (e) {
        dispatch({
            type: SET_WORKFLOW_ENABLED_ERROR,
            payload: { slug, previous, error: e.message },
        });
        throw e;
    }
};

export const renameWorkflow = (slug, name) => async dispatch => {
    dispatch({ type: RENAME_WORKFLOW_REQUEST, payload: { slug } });
    try {
        const res = await renameSocialWorkflow(slug, name);
        dispatch({
            type: RENAME_WORKFLOW_SUCCESS,
            payload: { slug, workflow: res.workflow },
        });
        return res.workflow;
    } catch (e) {
        dispatch({
            type: RENAME_WORKFLOW_ERROR,
            payload: { slug, error: e.message },
        });
        throw e;
    }
};

/**
 * Load the recent trigger DECISIONS for a workflow (the "Aktivität" panel). Fetched
 * on panel expand; keyed by workflow slug in state so each row keeps its own list.
 */
export const fetchTriggerLog = (slug, limit = 20) => async dispatch => {
    dispatch({ type: FETCH_TRIGGER_LOG_REQUEST, payload: slug });
    try {
        const res = await getSocialTriggerLog(slug, limit);
        dispatch({
            type: FETCH_TRIGGER_LOG_SUCCESS,
            payload: { slug, rows: res.data || [] },
        });
        return res.data || [];
    } catch (e) {
        dispatch({ type: FETCH_TRIGGER_LOG_ERROR, payload: { slug, error: e.message } });
    }
};

/**
 * Create a blank, paused workflow (one unset Auslöser). Resolves the new row so
 * the caller navigates straight into its studio.
 */
export const createWorkflow = name => async dispatch => {
    dispatch({ type: CREATE_WORKFLOW_REQUEST });
    try {
        const res = await createSocialWorkflow(name);
        dispatch({ type: CREATE_WORKFLOW_SUCCESS, payload: res.workflow });
        return res.workflow;
    } catch (e) {
        dispatch({ type: CREATE_WORKFLOW_ERROR, error: true, payload: e.message });
        throw e;
    }
};

/**
 * Duplicate a workflow's published graph into a new, paused workflow. Resolves the
 * new row so the caller navigates into its studio.
 */
export const duplicateWorkflow = (slug, name) => async dispatch => {
    dispatch({ type: DUPLICATE_WORKFLOW_REQUEST });
    try {
        const res = await duplicateSocialWorkflow(slug, name);
        dispatch({ type: DUPLICATE_WORKFLOW_SUCCESS, payload: res.workflow });
        return res.workflow;
    } catch (e) {
        dispatch({ type: DUPLICATE_WORKFLOW_ERROR, error: true, payload: e.message });
        throw e;
    }
};

/**
 * Hard-delete a workflow (by slug). OPTIMISTIC: the row is dropped immediately and
 * restored on error (e.g. a 409 while posts it minted are still in flight — the
 * server's German message surfaces as deleteWorkflowError).
 */
export const deleteWorkflow = slug => async (dispatch, getState) => {
    const restore = getState().AdminSocialPostsPage.workflows.find(w => w.slug === slug) || null;
    dispatch({ type: DELETE_WORKFLOW_REQUEST, payload: { slug } });
    try {
        const res = await deleteSocialWorkflow(slug);
        dispatch({ type: DELETE_WORKFLOW_SUCCESS, payload: { slug } });
        return res;
    } catch (e) {
        dispatch({
            type: DELETE_WORKFLOW_ERROR,
            payload: { slug, error: e.message, restore },
        });
        throw e;
    }
};

// ================ Design preset thunks ================ //

// Upsert a preset (by id) into a library list, preserving order.
const upsertPreset = (presets, preset) => {
    const i = presets.findIndex(p => p.id === preset.id);
    if (i === -1) return [...presets, preset];
    const next = presets.slice();
    next[i] = preset;
    return next;
};

export const normalizePresetName = name => {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return trimmed || 'Preset';
};

// Persist a preset list into settings.designPresets through the verified settings
// seam, then refresh the in-store library. Returns the saved (normalized) presets.
const persistPresetList = nextPresets => async (dispatch, getState) => {
    const { settings } = getState().AdminSocialPostsPage;
    const merged = { ...(settings || {}), designPresets: nextPresets };
    const saved = await updateSocialSettings(merged);
    const presets = normalizePresets(saved);
    dispatch({ type: FETCH_SETTINGS_SUCCESS, payload: saved });
    dispatch({ type: PRESETS_LOADED, payload: { presets } });
    return presets;
};

/**
 * Load the preset library for workflow design nodes. Fetches settings if absent
 * and normalizes the library, including migration from older per-platform templates.
 */
export const loadPresets = () => async (dispatch, getState) => {
    let { settings } = getState().AdminSocialPostsPage;
    if (!settings) settings = await dispatch(fetchSettings());
    const presets = normalizePresets(settings || {});
    dispatch({ type: PRESETS_LOADED, payload: { presets } });
    return presets;
};

/**
 * Persist a specific preset's edited doc into the library (the workflow design
 * node's "edit preset" path — the overlay holds its own doc state.
 */
export const savePresetDoc = (presetId, doc, name) => async (dispatch, getState) => {
    validateDesignDoc(doc);
    const { designPresets } = getState().AdminSocialPostsPage;
    const existing = findPreset(designPresets, presetId);
    const preset = {
        id: presetId,
        name: normalizePresetName(name != null ? name : existing && existing.name),
        doc,
    };
    return dispatch(persistPresetList(upsertPreset(designPresets, preset)));
};

/** Create a new preset at a named canvas size, persist it, and make it active. */
export const createPreset = (name, sizeKey = 'portrait') => async (dispatch, getState) => {
    const preset = makePreset(name, buildDefaultPreset(sizeKey));
    const { designPresets } = getState().AdminSocialPostsPage;
    await dispatch(persistPresetList([...designPresets, preset]));
    return preset;
};

/** Resize the current reviewer design doc (rescaling its layers) — committed like an edit. */
export const setPresetCanvas = (width, height) => (dispatch, getState) => {
    const { designDoc } = getState().AdminSocialPostsPage;
    if (!designDoc) return;
    dispatch(editDesign(rescaleDocToCanvas(designDoc, width, height)));
};

// ================ Reviewer per-post design (non-destructive /review) ================ //

/**
 * Seed the in-place editor with an arbitrary doc (the reviewer's PER-POST snapshot,
 * not a library preset). Clears undo/redo; presetId is null because this edit lives
 * on the post, never the shared library.
 */
export const loadDocIntoEditor = doc => ({
    type: DESIGN_LOAD,
    payload: { doc },
});

/**
 * Persist a reviewer's in-place design edit to ONE post channel — re-rendering it
 * server-side and refreshing the post in the store. The shared preset library is
 * untouched (this is a per-post override). Reuses the SAVE_DESIGN_* lifecycle so the
 * reviewer's collapse-on-save effect keeps working.
 *
 * `photo` ({ sha256, kind }) is a photo the reviewer staged via "Foto ersetzen":
 * the same save then switches the channel to it.
 */
export const saveReviewDesign = (postId, platform, doc, photo = null) => async dispatch => {
    dispatch({ type: SAVE_DESIGN_REQUEST });
    try {
        validateDesignDoc(doc); // instant client feedback; the server re-validates
        if (photo) await rerenderWithPhoto(postId, platform, doc, photo);
        else await rerenderSocialChannel(postId, platform, doc);
        // Refresh the post so the frame shows the freshly composed image + new spec.
        // The post read here is the one that contains the save; callers build on it.
        const post = await dispatch(fetchPost(postId));
        dispatch({ type: SAVE_DESIGN_SUCCESS, payload: {} });
        return { post: post || null };
    } catch (e) {
        dispatch({ type: SAVE_DESIGN_ERROR, error: true, payload: e.message });
        throw e;
    }
};

/**
 * Initial dashboard load. Admin API needs the browser auth cookie, so this is a
 * client-only fetch (no-op during SSR); the dashboard re-dispatches it on mount.
 */
export const loadData = (params, search) => dispatch => {
    if (typeof window === 'undefined') return Promise.resolve();
    const { post, tab, workflow } = parse(search || '');
    if (!isValidSocialPostsRouteTab(tab)) return Promise.resolve();
    const statuses = socialPostTabStatuses(tab);
    const tasks = [dispatch(fetchPendingCount()), dispatch(fetchTriggers())];
    // `?workflow=<slug>` scopes the list to one workflow (the dashboard filter).
    if (statuses) tasks.push(dispatch(queryPosts(statuses, { workflow: workflow || undefined })));
    if (post) tasks.push(dispatch(fetchPost(post)));
    return Promise.all(tasks);
};
